/**
 * <traceability-search>
 *
 * もとぶ牛トレーサビリティ検索のカスタム要素。
 * - フォーム送信を捕捉し、GAS の Web App エンドポイントへ GET で問い合わせる
 * - レスポンスに応じて検索結果カード / 該当なし / エラーメッセージを表示する
 *
 * 期待される API レスポンス:
 *   { "status": "found", "lot": "20260317-01", "processing_date": "2026/03/17",
 *     "cattle_count": 3, "cattle_ids": ["1234567890", ...], "staff": "...", "note": "..." }
 *   { "status": "not_found" }
 *   { "status": "error", "message": "..." }
 */

class TraceabilitySearch extends HTMLElement {
  constructor() {
    super();

    this.apiEndpoint = this.dataset.apiEndpoint || '';
    // PC / SP それぞれの個体識別番号 詳細ページ URL
    this.detailBaseUrlPc = this.dataset.detailBaseUrlPc || this.dataset.detailBaseUrl || '';
    this.detailBaseUrlSp = this.dataset.detailBaseUrlSp || this.detailBaseUrlPc;
    this.maxCattle = parseInt(this.dataset.maxCattle, 10) || 5;
    this.i18n = this._parseI18n(this.dataset.i18n);

    this.form = this.querySelector('[data-traceability-form]');
    this.input = this.querySelector('[data-traceability-input]');
    this.submitBtn = this.querySelector('[data-traceability-submit]');
    this.submitLabel = this.querySelector('[data-traceability-submit-label]');
    this.resultEl = this.querySelector('[data-traceability-result]');
    this.idInfoActionEl = this.querySelector('[data-traceability-id-info-action]');

    this._defaultSubmitLabel = this.submitLabel ? this.submitLabel.textContent : '';
    this._isLoading = false;
    this._abortController = null;

    // PC / SP 切り替え用 MediaQueryList
    const breakpoint = parseInt(this.dataset.mobileBreakpoint, 10) || 767;
    this._mql = (typeof window !== 'undefined' && window.matchMedia)
      ? window.matchMedia(`(max-width: ${breakpoint}px)`)
      : null;
    this._onMediaChange = this._onMediaChange.bind(this);

    if (this.form) {
      this.form.addEventListener('submit', this._onSubmit.bind(this));
    }
  }

  connectedCallback() {
    if (this._mql) {
      // 初期 href を viewport に合わせる
      this._updateAllDetailLinks();
      // viewport が PC <-> SP を跨ぐタイミングで href を更新
      if (this._mql.addEventListener) {
        this._mql.addEventListener('change', this._onMediaChange);
      } else if (this._mql.addListener) {
        // 古い Safari 向けフォールバック
        this._mql.addListener(this._onMediaChange);
      }
    }
  }

  disconnectedCallback() {
    if (this._abortController) {
      this._abortController.abort();
    }
    if (this._mql) {
      if (this._mql.removeEventListener) {
        this._mql.removeEventListener('change', this._onMediaChange);
      } else if (this._mql.removeListener) {
        this._mql.removeListener(this._onMediaChange);
      }
    }
  }

  _onMediaChange() {
    this._updateAllDetailLinks();
  }

  /**
   * 静的ボタン(.traceability-section__action) と既に描画済の個体識別番号リンク
   * (.traceability-cattle-list__link) の href を現在の viewport に合わせて更新する。
   */
  _updateAllDetailLinks() {
    // 静的ボタン
    if (this.idInfoActionEl) {
      const pc = this.idInfoActionEl.dataset.hrefPc || '';
      const sp = this.idInfoActionEl.dataset.hrefSp || pc;
      const url = this._getUrlForViewport(pc, sp);
      if (url) this.idInfoActionEl.setAttribute('href', url);
    }

    // 動的に描画された個体識別番号リンク（検索後に存在）
    const cattleLinks = this.querySelectorAll('.traceability-cattle-list__link');
    if (cattleLinks.length) {
      const url = this._getUrlForViewport(this.detailBaseUrlPc, this.detailBaseUrlSp);
      cattleLinks.forEach((link) => {
        if (url) link.setAttribute('href', url);
      });
    }
  }

  _getUrlForViewport(pcUrl, spUrl) {
    const isMobile = this._mql ? this._mql.matches : false;
    if (isMobile) return spUrl || pcUrl || '';
    return pcUrl || spUrl || '';
  }

  /* ----------------------------------------------------------------------- */
  /* Event handlers                                                          */
  /* ----------------------------------------------------------------------- */

  _onSubmit(event) {
    event.preventDefault();
    if (this._isLoading) return;

    const lot = (this.input.value || '').trim();
    if (!lot) {
      this.input.focus();
      return;
    }

    this._search(lot);
  }

  /* ----------------------------------------------------------------------- */
  /* API call                                                                */
  /* ----------------------------------------------------------------------- */

  async _search(lot) {
    if (!this.apiEndpoint) {
      this._renderError(this.i18n.error);
      return;
    }

    this._setLoading(true);
    this._renderLoading();

    if (this._abortController) {
      this._abortController.abort();
    }
    this._abortController = new AbortController();

    try {
      const url = `${this.apiEndpoint}${this.apiEndpoint.includes('?') ? '&' : '?'}lot=${encodeURIComponent(lot)}`;
      const response = await fetch(url, {
        method: 'GET',
        redirect: 'follow',
        signal: this._abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      console.log("data",data,"lot",lot);
      this._handleResponse(data, lot);
    } catch (err) {
      if (err.name === 'AbortError') return;
      // eslint-disable-next-line no-console
      console.error('[traceability-search] fetch failed:', err);
      this._renderError(this.i18n.error);
    } finally {
      this._setLoading(false);
    }
  }

  _handleResponse(data, lot) {
    if (!data || typeof data !== 'object') {
      this._renderError(this.i18n.error);
      return;
    }

    switch (data.status) {
      case 'found':
        this._renderFound(data, lot);
        break;
      case 'not_found':
        this._renderNotFound();
        break;
      case 'error':
        this._renderError(data.message || this.i18n.error);
        break;
      default:
        this._renderError(this.i18n.error);
    }
  }

  /* ----------------------------------------------------------------------- */
  /* Render                                                                  */
  /* ----------------------------------------------------------------------- */

  _renderLoading() {
    if (!this.resultEl) return;
    this.resultEl.hidden = false;
    this.resultEl.innerHTML = `
      <div class="traceability-search-result__status" role="status">
        ${this._escape(this.i18n.loading)}
      </div>
    `;
  }

  _renderNotFound() {
    if (!this.resultEl) return;
    this.resultEl.hidden = false;
    this.resultEl.innerHTML = `
      <div class="traceability-search-result__status" role="status">
        ${this._escape(this.i18n.notFound)}
      </div>
    `;
  }

  _renderError(message) {
    if (!this.resultEl) return;
    this.resultEl.hidden = false;
    this.resultEl.innerHTML = `
      <div class="traceability-search-result__status traceability-search-result__status--error" role="alert">
        ${this._escape(message || this.i18n.error)}
      </div>
    `;
  }

  _renderFound(data, lot) {
    if (!this.resultEl) return;

    const lotValue = data.lot || lot || '';
    // 加工日: 実 API は `date`、念のため `processing_date` も後方互換で許容
    const processingDate = data.date || data.processing_date || '';
    // 個体ID 一覧: 実 API は `cattle`、後方互換で `cattle_ids` も許容
    const cattleIds = Array.isArray(data.cattle)
      ? data.cattle
      : (Array.isArray(data.cattle_ids) ? data.cattle_ids : []);
    // 使用個体数: API には数値フィールドが無いので配列長から算出（明示指定があれば優先）
    const cattleCount = data.cattle_count != null
      ? String(data.cattle_count)
      : String(cattleIds.length);
    const staff = data.staff || '';
    const note = (data.note != null && String(data.note).length > 0) ? String(data.note) : this.i18n.noNote;

    const cattleListHtml = this._renderCattleList(cattleIds);

    this.resultEl.hidden = false;
    this.resultEl.innerHTML = `
      <article class="traceability-result-card" aria-labelledby="${this.id || 'traceability-search'}-result-heading">
        <header class="traceability-result-card__heading">
          <span class="traceability-result-card__heading-label">
            ${this._escape(this.i18n.resultHeading)} / ${this._escape(this.i18n.lotPrefix)}
          </span>
          <span class="traceability-result-card__heading-value" id="${this.id || 'traceability-search'}-result-heading">
            ${this._escape(lotValue)}
          </span>
        </header>

        <dl class="traceability-result-card__list">
          <div class="traceability-result-card__row">
            <dt>${this._escape(this.i18n.processingDate)}</dt>
            <dd>${this._escape(processingDate)}</dd>
          </div>

          <div class="traceability-result-card__row">
            <dt>${this._escape(this.i18n.cattleCount)}</dt>
            <dd>${this._escape(cattleCount)}${this._escape(this.i18n.cattleUnit)}</dd>
          </div>

          <div class="traceability-result-card__row">
            <dt>${this._escape(this.i18n.cattleList)}</dt>
            <dd>${cattleListHtml}</dd>
          </div>

          <div class="traceability-result-card__row">
            <dt>${this._escape(this.i18n.staff)}</dt>
            <dd>${this._escape(staff)}</dd>
          </div>

          <div class="traceability-result-card__row">
            <dt>${this._escape(this.i18n.note)}</dt>
            <dd>${this._escape(note)}</dd>
          </div>
        </dl>
      </article>
    `;
  }

  _renderCattleList(cattleIds) {
    if (!cattleIds.length) {
      return `<span>${this._escape(this.i18n.noNote)}</span>`;
    }

    const limited = cattleIds.slice(0, this.maxCattle);
    // 描画時点の viewport に応じた URL を採用（その後の resize は _onMediaChange で追従）
    const detailUrl = this._getUrlForViewport(this.detailBaseUrlPc, this.detailBaseUrlSp) || '#';
    const items = limited.map((id) => {
      const safeId = this._escape(String(id));
      return `
        <li class="traceability-cattle-list__item">
          <span class="traceability-cattle-list__id">${safeId}</span>
          <a class="traceability-cattle-list__link"
             href="${this._escape(detailUrl)}"
             target="_blank"
             rel="noopener noreferrer">
            <span>${this._escape(this.i18n.detailLink)}</span>
            <svg aria-hidden="true" focusable="false" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
              <polyline points="15 3 21 3 21 9"/>
              <line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
          </a>
        </li>
      `;
    }).join('');

    return `<ul class="traceability-cattle-list" role="list">${items}</ul>`;
  }

  /* ----------------------------------------------------------------------- */
  /* Helpers                                                                 */
  /* ----------------------------------------------------------------------- */

  _setLoading(isLoading) {
    this._isLoading = isLoading;
    if (!this.submitBtn) return;

    if (isLoading) {
      this.submitBtn.setAttribute('aria-busy', 'true');
      this.submitBtn.setAttribute('disabled', '');
      if (this.submitLabel) {
        this.submitLabel.textContent = this.i18n.loading || this._defaultSubmitLabel;
      }
    } else {
      this.submitBtn.removeAttribute('aria-busy');
      this.submitBtn.removeAttribute('disabled');
      if (this.submitLabel) {
        this.submitLabel.textContent = this._defaultSubmitLabel;
      }
    }
  }

  _parseI18n(raw) {
    const fallback = {
      processingDate: '加工日',
      cattleCount: '使用個体',
      cattleList: '識別番号一覧',
      staff: '担当者',
      note: '備考',
      detailLink: '詳細を見る',
      notFound: 'ロット番号が見つかりません',
      error: 'エラーが発生しました。時間をおいて再度お試しください。',
      loading: '検索中...',
      submit: '検索する',
      cattleUnit: '頭',
      resultHeading: '検索結果',
      lotPrefix: 'ロット番号',
      noNote: '—',
    };
    if (!raw) return fallback;
    try {
      const parsed = JSON.parse(raw);
      return Object.assign({}, fallback, parsed);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[traceability-search] invalid i18n JSON, using fallback', err);
      return fallback;
    }
  }

  _escape(value) {
    if (value == null) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

if (!customElements.get('traceability-search')) {
  customElements.define('traceability-search', TraceabilitySearch);
}
